import React, { useState } from 'react';
// import { useGlobal } from 'reactn';
import { connect } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { Tooltip } from 'antd';
import styled from 'styled-components';
import Modal from '../../common/components/Modal';
import Button from '../../common/components/Button';
import Popconfirm from '../../common/components/Popconfirm';
import { showError } from '../../common/notifications';
import { GET_DELIVERABLES_BY_PROJECT_ID, GET_PROJECT_BY_ID } from '../queries';
import UpdateDeliverableForm from '../../common/components/UpdateDeliverableForm';
import CopyDeliverableForm from '../../common/components/UpdateDeliverableForm/CopyDeliverableForm';
import CopyMilestoneDeliverablesForm from '../../common/components/UpdateDeliverableForm/CopyMilestoneDeliverablesForm';
import useTaxonomy from '../../../hooks/useTaxonomy';

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const REMOVE_PROJECT_DELIVERABLE = gql`
  mutation removeDeliverable($deliverableId: ID!) {
    removeDeliverable(deliverableId: $deliverableId)
  }
`;

const ProjectDetailsDeliverableActions = ({ deliverable, milestoneNames, refetchQueries, netSuiteEnabled }) => {
  // const [lockEditing] = useGlobal('lockEditing');
  const [updateDeliverableVisible, setUpdateDeliverableVisible] = useState(false);
  const [copyDeliverableVisible, setCopyDeliverableVisible] = useState(false);
  const [copyMilestoneVisible, setCopyMilestoneVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeDeliverable] = useMutation(REMOVE_PROJECT_DELIVERABLE);
  const {
    projectId,
    _id: deliverableId,
    key,
    milestoneName,
    children = [],
    milestoneBudget,
    disabled,
    trackAgainstTimeOff,
  } = deliverable;

  const isMilestone = key === milestoneName;
  const childrenHaveBudget = children?.some(c => c.budget);
  const timeOffTaxonomy = useTaxonomy("Time Off");

  return (
    <ActionsWrapper>
      <div className="flex flex-start">
        {!childrenHaveBudget && (
          <Button
            // disabled={lockEditing}
            className="mr-2"
            onClick={() => setUpdateDeliverableVisible(true)}
          >
            Edit
          </Button>
        )}
        {isMilestone && children && children.length > 0 && !netSuiteEnabled && (
          <Tooltip title="Copy all deliverables in this milestone">
            <Button
              className="mr-2"
              onClick={() => setCopyMilestoneVisible(true)}
              icon="copy"
            >
            </Button>
          </Tooltip>
        )}
        {(!isMilestone || !children) && (
          <>
            {!netSuiteEnabled && (
              <Tooltip title="Copy this deliverable">
                <Button
                  className="mr-2"
                  onClick={() => setCopyDeliverableVisible(true)}
                  icon="copy"
                >
                </Button>
              </Tooltip>
            )}
            <Popconfirm
              placement="topLeft"
              title="Are you sure you want to delete this deliverable?"
              disabled={deliverable.totalApprovedHours > 0}
              onConfirm={async () => {
                setIsRemoving(true);
                const result = await removeDeliverable({
                  variables: { deliverableId },
                  refetchQueries: [
                    {
                      query: GET_DELIVERABLES_BY_PROJECT_ID,
                      variables: {
                        projectId,
                      },
                    },
                    {
                      query: GET_PROJECT_BY_ID,
                      variables: {
                        projectId,
                      },
                    },
                  ],
                  awaitRefetchQueries: true,
                });

                if (result.errors) {
                  showError({ message: result.errors[0].message });
                  setIsRemoving(false);
                }
              }}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip
                title={
                  deliverable.totalApprovedHours > 0 &&
                  'Cannot delete deliverable. Approved hours have already been submitted to it.'
                }
              >
                <Button
                  disabled={deliverable.totalApprovedHours > 0}
                  type="danger"
                  loading={isRemoving}
                  icon="close"
                ></Button>
              </Tooltip>
            </Popconfirm>
          </>
        )}
      </div>
      <Modal
        title="Update Deliverable"
        visible={updateDeliverableVisible}
        destroyOnClose
        onOk={() => setUpdateDeliverableVisible(false)}
        onCancel={() => setUpdateDeliverableVisible(false)}
        footer={null}
      >
        <UpdateDeliverableForm
          isMilestone={isMilestone}
          milestoneBudget={milestoneBudget}
          deliverable={deliverable}
          refetchQueries={[...refetchQueries, {
            query: GET_PROJECT_BY_ID,
            variables: {
              projectId,
            },
          },
          ]}
          onComplete={() => setUpdateDeliverableVisible(false)}
          milestoneNames={milestoneNames}
          disabled={disabled}
          trackAgainstTimeOff={trackAgainstTimeOff}
          timeOffTaxonomy={timeOffTaxonomy}
        />
      </Modal>
      <Modal
        title="Copy Deliverable"
        visible={copyDeliverableVisible}
        destroyOnClose
        onOk={() => setCopyDeliverableVisible(false)}
        onCancel={() => setCopyDeliverableVisible(false)}
        footer={null}
      >
        <CopyDeliverableForm
          projectId={projectId}
          deliverable={deliverable}
          refetchQueries={refetchQueries}
          onComplete={() => setCopyDeliverableVisible(false)}
          milestoneNames={milestoneNames}
        />
      </Modal>
      <Modal
        title="Copy All Deliverables"
        visible={copyMilestoneVisible}
        destroyOnClose
        onOk={() => setCopyMilestoneVisible(false)}
        onCancel={() => setCopyMilestoneVisible(false)}
        footer={null}
        width={700}
      >
        <CopyMilestoneDeliverablesForm
          projectId={projectId}
          children={children}
          milestoneName={milestoneName}
          refetchQueries={refetchQueries}
          onComplete={() => setCopyMilestoneVisible(false)}
          milestoneNames={milestoneNames}
        />
      </Modal>
    </ActionsWrapper>
  );
};

const mapStateToProps = state => ({
  user: state.meteor?.user,
});

export default connect(mapStateToProps)(ProjectDetailsDeliverableActions);
